import {
  useState,
  useMemo,
  Fragment,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  Paper,
  TextInput,
  Textarea,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Tabs,
  Checkbox,
  NumberInput,
  Divider,
  SimpleGrid,
  Image,
  Alert,
  Menu,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconRefresh,
  IconChevronDown,
  IconChevronRight,
  IconStar,
  IconStarFilled,
  IconX,
  IconCheck,
  IconAlertCircle,
  IconInfoCircle,
  IconDotsVertical,
} from "@tabler/icons-react";
import {
  usePractices,
  useCreatePractice,
  useUpdatePractice,
  useTogglePracticeActive,
  usePracticePhysicians,
  useAddPracticePhysician,
  useRemovePracticePhysician,
  useSetPrimaryPracticePhysician,
  usePracticeReportDeliveryMethods,
  useAddPracticeReportDeliveryMethod,
  useUpdatePracticeReportDeliveryMethod,
  useRemovePracticeReportDeliveryMethod,
  useTogglePracticeReportDeliveryMethodActive,
} from "../../../hooks/admin/usePractices";
import { usePhysiciansAdmin } from "../../../hooks/admin/usePhysicians";
import { usePathologists } from "../../../hooks/useAdmin";
import { useReportDeliveryMethodsAdmin } from "../../../hooks/admin/useReportDeliveryMethods";
import PracticeForm, {
  PracticeFormValues,
} from "../../../components/admin/practices/PracticeForm";
import { PortalAccessSection } from "../../../components/admin/portal-access/PortalAccessSection";
import { AddPortalGrantModal } from "../../../components/admin/portal-access/AddPortalGrantModal";
import { PortalAccessIndicator } from "../../../components/admin/portal-access/PortalAccessIndicator";
import { usePortalAccessSubjectIdSet } from "../../../hooks/admin/usePortalAccess";
import {
  Practice,
  PracticeReportDeliveryMethod,
  CreatePracticeReportDeliveryMethodInput,
  UpdatePracticeReportDeliveryMethodInput,
  practicesService,
} from "../../../services/admin/practices.service";
import {
  required,
  email,
  phone,
  fax,
  scrollToFirstError,
} from "../../../utils/formValidation";
import { sanitizeReportFooterHtml } from "../../../utils/sanitize";

interface PracticeFilters {
  status?: "all" | "active" | "inactive";
  preferredPathologistId?: string;
}

const ITEMS_PER_PAGE = 20;

function LogoPreview({
  logoS3Key,
  practiceName,
}: {
  logoS3Key: string | null;
  practiceName?: string;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoS3Key) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    practicesService
      .getLogoDownloadUrl(logoS3Key)
      .then((url) => {
        if (!cancelled) setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [logoS3Key]);

  if (!logoS3Key) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100%",
          background: "var(--mantine-color-gcpBlue-6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text fw={700} size="xl" c="white">
          {practiceName?.charAt(0)?.toUpperCase() || "P"}
        </Text>
      </Box>
    );
  }
  if (!previewUrl) return <Loader size="xs" />;
  return (
    <Image
      src={previewUrl}
      alt="Practice logo"
      fit="contain"
      style={{ maxHeight: "100%", maxWidth: "100%" }}
      onError={() => setPreviewUrl(null)}
    />
  );
}

export default function PracticesAdminPage() {
  // Modal states
  const [
    practiceModalOpened,
    { open: openPracticeModal, close: closePracticeModal },
  ] = useDisclosure(false);
  const [
    physicianModalOpened,
    { open: openPhysicianModal, close: closePhysicianModal },
  ] = useDisclosure(false);
  const [
    deliveryModalOpened,
    { open: openDeliveryModal, close: closeDeliveryModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    portalGrantModalOpened,
    { open: openPortalGrantModal, close: closePortalGrantModal },
  ] = useDisclosure(false);

  // URL state
  const [searchParams, setSearchParams] = useSearchParams();

  const VALID_STATUSES = ["active", "inactive"] as const;
  const VALID_TABS = [
    "portal-access",
    "physicians",
    "report-delivery",
  ] as const;

  const urlSearch = searchParams.get("search") ?? "";
  const urlStatus = VALID_STATUSES.includes(
    searchParams.get("status") as (typeof VALID_STATUSES)[number]
  )
    ? (searchParams.get("status") as "active" | "inactive")
    : "all";
  const urlPathologist = searchParams.get("pathologist") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const expandedRow = searchParams.get("practice") ?? null;
  const activeTab = VALID_TABS.includes(
    searchParams.get("tab") as (typeof VALID_TABS)[number]
  )
    ? (searchParams.get("tab") as string)
    : "portal-access";

  const filters = useMemo<PracticeFilters>(
    () => ({ status: urlStatus, preferredPathologistId: urlPathologist }),
    [urlStatus, urlPathologist]
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Page state
  const [editingPractice, setEditingPractice] = useState<Practice | null>(null);
  const [pendingFilters, setPendingFilters] = useState<PracticeFilters>({
    status: "all",
    preferredPathologistId: "",
  });

  // Local input state for search (responsive typing; URL updated on debounce)
  const [inputSearch, setInputSearch] = useState(urlSearch);
  const [debouncedSearch] = useDebouncedValue(inputSearch, 300);

  // Sync input when URL changes externally (back/forward navigation)
  const prevUrlSearch = useRef(urlSearch);
  useEffect(() => {
    if (urlSearch !== prevUrlSearch.current) {
      prevUrlSearch.current = urlSearch;
      setInputSearch(urlSearch);
    }
  }, [urlSearch]);

  // Write debounced search to URL and reset page
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    updateParams({ search: debouncedSearch || null, page: null });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Modal form states
  const [selectedPhysicianId, setSelectedPhysicianId] = useState<string>("");
  const [physicianIsPrimary, setPhysicianIsPrimary] = useState(false);
  const [editingDelivery, setEditingDelivery] =
    useState<PracticeReportDeliveryMethod | null>(null);
  const [deliveryPhysicianId, setDeliveryPhysicianId] = useState<string>("");

  // Delivery method form
  interface DeliveryFormValues {
    reportDeliveryMethodId: string;
    email: string;
    fax: string;
    phone: string;
    address: string;
    hl7EndpointHost: string;
    hl7EndpointPort: number | string;
    hl7FacilityId: string;
    hl7ApplicationId: string;
    remoteConnectionIdentifier: string;
  }

  const [itemToDelete, setItemToDelete] = useState<{
    type: "physician" | "delivery";
    id: string;
    name: string;
  } | null>(null);

  // Practice hooks
  const { data: practices = [], isLoading, refetch } = usePractices();
  const {
    subjectIdSet: practicesWithPortal,
    activeIdSet: practicesWithActivePortal,
  } = usePortalAccessSubjectIdSet("practice");
  const createPractice = useCreatePractice();
  const updatePractice = useUpdatePractice();
  const togglePracticeActive = useTogglePracticeActive();

  // Reference data hooks
  const { data: allPhysicians = [] } = usePhysiciansAdmin();
  const { data: deliveryMethods = [] } = useReportDeliveryMethodsAdmin();
  const { data: pathologists = [] } = usePathologists();

  const deliveryForm = useForm<DeliveryFormValues>({
    initialValues: {
      reportDeliveryMethodId: "",
      email: "",
      fax: "",
      phone: "",
      address: "",
      hl7EndpointHost: "",
      hl7EndpointPort: "",
      hl7FacilityId: "",
      hl7ApplicationId: "",
      remoteConnectionIdentifier: "",
    },
    validate: {
      reportDeliveryMethodId: required("Delivery method is required"),
      email: email(),
      fax: fax(),
      phone: phone(),
      hl7EndpointHost: (value, values) => {
        const method = deliveryMethods.find(
          (m) => m.id === values.reportDeliveryMethodId
        );
        if (method?.name?.toLowerCase().startsWith("hl7") && !value?.trim()) {
          return "HL7 endpoint host is required";
        }
        return null;
      },
      hl7EndpointPort: (value, values) => {
        const method = deliveryMethods.find(
          (m) => m.id === values.reportDeliveryMethodId
        );
        if (
          method?.name?.toLowerCase().startsWith("hl7") &&
          (!value || value === "")
        ) {
          return "HL7 endpoint port is required";
        }
        return null;
      },
      remoteConnectionIdentifier: (value, values) => {
        const method = deliveryMethods.find(
          (m) => m.id === values.reportDeliveryMethodId
        );
        if (
          method?.name?.toLowerCase().includes("remote print") &&
          !value?.trim()
        ) {
          return "Connection identifier is required for Remote Print delivery";
        }
        return null;
      },
    },
  });

  const { data: practicePhysicians = [], isLoading: isLoadingPhysicians } =
    usePracticePhysicians(expandedRow);

  const duplicatePhysicianNames = useMemo(() => {
    const counts = new Map<string, number>();
    practicePhysicians.forEach((pp) => {
      const name =
        `${pp.physician.firstName} ${pp.physician.lastName}`.toLowerCase();
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([name]) => name)
    );
  }, [practicePhysicians]);

  const { data: practiceDeliveryMethods = [], isLoading: isLoadingDelivery } =
    usePracticeReportDeliveryMethods(expandedRow);

  // Edit modal loading state
  const isLoadingEditData = false;

  // Mutation hooks
  const addPhysician = useAddPracticePhysician();
  const removePhysician = useRemovePracticePhysician();
  const setPrimaryPhysician = useSetPrimaryPracticePhysician();
  const addDeliveryMethod = useAddPracticeReportDeliveryMethod();
  const updateDeliveryMethod = useUpdatePracticeReportDeliveryMethod();
  const removeDeliveryMethod = useRemovePracticeReportDeliveryMethod();
  const toggleDeliveryActive = useTogglePracticeReportDeliveryMethodActive();

  // Filter practices
  const filteredPractices = useMemo(() => {
    let result = practices;

    // Apply search filter
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      result = result.filter(
        (p) =>
          p.name?.toLowerCase().includes(query) ||
          p.shortName?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filters.status === "active") {
      result = result.filter((p) => p.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((p) => !p.isActive);
    }

    // Apply preferred pathologist filter
    if (filters.preferredPathologistId) {
      result = result.filter(
        (p) => p.preferredPathologistId === filters.preferredPathologistId
      );
    }

    return result;
  }, [practices, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredPractices.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredPractices.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPractices, page]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.status !== "all" || !!filters.preferredPathologistId;

  // Get linked IDs to filter dropdowns
  const linkedPhysicianIds = practicePhysicians.map((pp) => pp.physicianId);

  // Available options for modals
  const availablePhysicians = allPhysicians.filter(
    (p) => p.isActive && !linkedPhysicianIds.includes(p.id)
  );

  // Handlers for Practices
  const handleAddPractice = () => {
    setEditingPractice(null);
    openPracticeModal();
  };

  const handleEditPractice = (practice: Practice) => {
    setEditingPractice(practice);
    openPracticeModal();
  };

  const handlePracticeSubmit = async (values: PracticeFormValues) => {
    const sanitizedValues = {
      name: values.name,
      shortName: values.shortName?.trim() || undefined,
      primaryPhone: values.primaryPhone?.trim() || undefined,
      secondaryPhone: values.secondaryPhone?.trim() || undefined,
      fax: values.fax?.trim() || undefined,
      address: values.address?.trim() || null,
      email: values.email?.trim() || null,
      medicalDirector: values.medicalDirector?.trim() || null,
      footerHtml: sanitizeReportFooterHtml(values.footerHtml) || null,
      preferredPathologistId:
        values.preferredPathologistId?.trim() || undefined,
      logoS3Key: values.logoS3Key ?? null,
    };

    if (editingPractice) {
      await updatePractice.mutateAsync({
        id: editingPractice.id,
        ...sanitizedValues,
      });
    } else {
      await createPractice.mutateAsync(sanitizedValues);
    }
    closePracticeModal();
  };

  const handleTogglePracticeActive = async (practice: Practice) => {
    await togglePracticeActive.mutateAsync({
      id: practice.id,
      isActive: !practice.isActive,
    });
  };

  const toggleRow = (practiceId: string) => {
    if (expandedRow === practiceId) {
      updateParams({ practice: null, tab: null });
    } else {
      updateParams({ practice: practiceId, tab: null });
    }
  };

  // Physician handlers
  const handleOpenPhysicianModal = () => {
    setSelectedPhysicianId("");
    setPhysicianIsPrimary(false);
    openPhysicianModal();
  };

  const handleAddPhysician = async () => {
    if (!expandedRow || !selectedPhysicianId) return;
    await addPhysician.mutateAsync({
      practiceId: expandedRow,
      input: {
        physicianId: selectedPhysicianId,
        isPrimary: physicianIsPrimary,
      },
    });
    closePhysicianModal();
  };

  const handleSetPrimaryPhysician = async (physicianId: string) => {
    if (!expandedRow) return;
    await setPrimaryPhysician.mutateAsync({
      practiceId: expandedRow,
      physicianId,
    });
  };

  // Delivery method handlers
  const handleOpenDeliveryModal = (delivery?: PracticeReportDeliveryMethod) => {
    if (delivery) {
      setEditingDelivery(delivery);
      deliveryForm.setValues({
        reportDeliveryMethodId: delivery.reportDeliveryMethodId,
        email: delivery.email || "",
        fax: delivery.fax || "",
        phone: delivery.phone || "",
        address: delivery.address || "",
        hl7EndpointHost: delivery.hl7EndpointHost || "",
        hl7EndpointPort: delivery.hl7EndpointPort || "",
        hl7FacilityId: delivery.hl7FacilityId || "",
        hl7ApplicationId: delivery.hl7ApplicationId || "",
        remoteConnectionIdentifier: delivery.remoteConnectionIdentifier || "",
      });
      setDeliveryPhysicianId("");
    } else {
      setEditingDelivery(null);
      setDeliveryPhysicianId("");
      // Prefill from current practice's contact info
      const currentPractice = practices.find((p) => p.id === expandedRow);
      deliveryForm.setValues({
        reportDeliveryMethodId: "",
        email: currentPractice?.email || "",
        fax: currentPractice?.fax || "",
        phone: currentPractice?.primaryPhone || "",
        address: currentPractice?.address || "",
        hl7EndpointHost: currentPractice?.hl7EndpointHost || "",
        hl7EndpointPort: currentPractice?.hl7EndpointPort || "",
        hl7FacilityId: currentPractice?.hl7FacilityId || "",
        hl7ApplicationId: currentPractice?.hl7ApplicationId || "",
        remoteConnectionIdentifier: "",
      });
    }
    openDeliveryModal();
  };

  const handleDeliverySubmit = deliveryForm.onSubmit(
    async (values) => {
      if (!expandedRow) return;

      const deliveryMethodInput: Omit<
        CreatePracticeReportDeliveryMethodInput,
        "physicianId"
      > = {
        reportDeliveryMethodId: values.reportDeliveryMethodId,
        email: values.email || null,
        fax: values.fax || null,
        phone: values.phone || null,
        address: values.address || null,
        hl7EndpointHost: values.hl7EndpointHost?.trim() || null,
        hl7EndpointPort:
          typeof values.hl7EndpointPort === "number"
            ? values.hl7EndpointPort
            : null,
        hl7FacilityId: values.hl7FacilityId?.trim() || null,
        hl7ApplicationId: values.hl7ApplicationId?.trim() || null,
        remoteConnectionIdentifier:
          values.remoteConnectionIdentifier?.trim() || null,
      };

      try {
        if (editingDelivery) {
          const input: UpdatePracticeReportDeliveryMethodInput = {
            id: editingDelivery.id,
            ...deliveryMethodInput,
          };
          await updateDeliveryMethod.mutateAsync({
            practiceId: expandedRow,
            input,
          });
        } else {
          const input: CreatePracticeReportDeliveryMethodInput = {
            ...deliveryMethodInput,
            physicianId: deliveryPhysicianId || null,
          };
          await addDeliveryMethod.mutateAsync({
            practiceId: expandedRow,
            input,
          });
        }
        closeDeliveryModal();
      } catch {
        // Portal-without-access is prevented in the UI (see portalNeedsSetup),
        // so submission can't reach the backend's NO_PORTAL_ACCESS guard. Any
        // other failure is surfaced by the mutation's onError toast.
      }
    },
    () => scrollToFirstError(deliveryForm)
  );

  const handleCloseDeliveryModal = useCallback(() => {
    closeDeliveryModal();
  }, [closeDeliveryModal]);

  const handleOpenPortalGrantModal = useCallback(() => {
    openPortalGrantModal();
  }, [openPortalGrantModal]);

  const handleClosePortalGrantModal = useCallback(() => {
    closePortalGrantModal();
  }, [closePortalGrantModal]);

  const handleToggleDeliveryActive = async (id: string) => {
    if (!expandedRow) return;
    await toggleDeliveryActive.mutateAsync({ practiceId: expandedRow, id });
  };

  // Delete handlers
  const handleConfirmDelete = (
    type: "physician" | "delivery",
    id: string,
    name: string
  ) => {
    setItemToDelete({ type, id, name });
    openDeleteModal();
  };

  const handleDelete = async () => {
    if (!expandedRow || !itemToDelete) return;

    if (itemToDelete.type === "physician") {
      await removePhysician.mutateAsync({
        practiceId: expandedRow,
        physicianId: itemToDelete.id,
      });
    } else if (itemToDelete.type === "delivery") {
      await removeDeliveryMethod.mutateAsync({
        practiceId: expandedRow,
        id: itemToDelete.id,
      });
    }

    closeDeleteModal();
    setItemToDelete(null);
  };

  // Filter handlers
  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    updateParams({
      status:
        pendingFilters.status !== "all" ? pendingFilters.status ?? null : null,
      pathologist: pendingFilters.preferredPathologistId || null,
      page: null,
    });
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    const defaultFilters: PracticeFilters = {
      status: "all",
      preferredPathologistId: "",
    };
    setPendingFilters(defaultFilters);
    updateParams({ status: null, pathologist: null, page: null });
  };

  // Get the current selected delivery method for conditional fields
  const selectedMethod = deliveryMethods.find(
    (m) => m.id === deliveryForm.values.reportDeliveryMethodId
  );
  const methodName = selectedMethod?.name?.toLowerCase() || "";

  // Portal delivery needs at least one eligible portal grant on the practice.
  // Surface the warning (and suppress the submit button) the moment Portal is
  // chosen and none exists — no need to attempt a save first.
  const portalNeedsSetup =
    methodName.startsWith("portal") &&
    !!expandedRow &&
    !practicesWithPortal.has(expandedRow);

  const portalIsPending =
    methodName.startsWith("portal") &&
    !!expandedRow &&
    practicesWithPortal.has(expandedRow) &&
    !practicesWithActivePortal.has(expandedRow);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Practices</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage practices and their physicians, accessioning types, and
                report delivery methods
              </Text>
            </div>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleAddPractice}
            >
              Add Practice
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or short name..."
                  value={inputSearch}
                  onChange={(e) => setInputSearch(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status !== "all" && (
                      <Badge variant="light" color="blue">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    {filters.preferredPathologistId && (
                      <Badge variant="light" color="grape">
                        Pathologist:{" "}
                        {pathologists.find(
                          (p) => p.id === filters.preferredPathologistId
                        )?.fullName || "Selected"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredPractices.length} practice
                  {filteredPractices.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Practices ({filteredPractices.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredPractices.length
                      )}{" "}
                      of {filteredPractices.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={(n) =>
                        updateParams({ page: n > 1 ? String(n) : null })
                      }
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredPractices.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || inputSearch
                    ? "No practices match the current filters"
                    : "No practices found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }} />
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Short Name</Table.Th>
                      <Table.Th>Preferred Pathologist</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((practice) => (
                      <Fragment key={practice.id}>
                        <Table.Tr>
                          {/* Expand Toggle */}
                          <Table.Td>
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={() => toggleRow(practice.id)}
                            >
                              {expandedRow === practice.id ? (
                                <IconChevronDown size={16} />
                              ) : (
                                <IconChevronRight size={16} />
                              )}
                            </ActionIcon>
                          </Table.Td>

                          {/* Name */}
                          <Table.Td>
                            <Group gap="xs" wrap="nowrap" align="center">
                              <PortalAccessIndicator
                                hasAccess={practicesWithPortal.has(practice.id)}
                                subjectLabel={practice.name}
                              />
                              <Text size="sm" fw={500}>
                                {practice.name}
                              </Text>
                            </Group>
                          </Table.Td>

                          {/* Short Name */}
                          <Table.Td>
                            <Text size="sm">{practice.shortName || "—"}</Text>
                          </Table.Td>

                          {/* Preferred Pathologist */}
                          <Table.Td>
                            <Text size="sm">
                              {practice.preferredPathologist?.fullName || "—"}
                            </Text>
                          </Table.Td>

                          {/* Status */}
                          <Table.Td>
                            <Badge
                              color={practice.isActive ? "green" : "red"}
                              variant="light"
                            >
                              {practice.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </Table.Td>

                          {/* Actions */}
                          <Table.Td>
                            <Menu
                              position="bottom-end"
                              offset={4}
                              withinPortal
                              shadow="md"
                            >
                              <Menu.Target>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  aria-label="Row actions"
                                >
                                  <IconDotsVertical size={18} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  leftSection={<IconEdit size={16} />}
                                  onClick={() => handleEditPractice(practice)}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={
                                    practice.isActive ? (
                                      <IconX size={16} />
                                    ) : (
                                      <IconCheck size={16} />
                                    )
                                  }
                                  color={practice.isActive ? "red" : "green"}
                                  onClick={() =>
                                    void handleTogglePracticeActive(practice)
                                  }
                                >
                                  {practice.isActive
                                    ? "Deactivate"
                                    : "Activate"}
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Table.Td>
                        </Table.Tr>

                        {/* Expanded Row - Tabbed Sections */}
                        {expandedRow === practice.id && (
                          <Table.Tr>
                            <Table.Td colSpan={6} p={0}>
                              <Box pl="xl" pr="md" pb="md" pt="sm" bg="gray.0">
                                {/* Practice detail strip */}
                                <Group
                                  align="flex-start"
                                  gap="xl"
                                  mb="md"
                                  pb="sm"
                                  style={{
                                    borderBottom:
                                      "1px solid var(--mantine-color-gray-3)",
                                  }}
                                >
                                  {/* Practice logo / avatar */}
                                  <Box
                                    style={{
                                      width: 80,
                                      height: 80,
                                      flexShrink: 0,
                                      border:
                                        "2px solid var(--mantine-color-gcpBlue-2)",
                                      borderRadius: 12,
                                      overflow: "hidden",
                                      background: "#fff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <LogoPreview
                                      logoS3Key={practice.logoS3Key}
                                      practiceName={practice.name}
                                    />
                                  </Box>

                                  {/* Detail fields */}
                                  <Stack
                                    gap={4}
                                    style={{ flex: 1, minWidth: 0 }}
                                  >
                                    {/* Email — full width so it never needs to wrap */}
                                    {practice.email && (
                                      <Group gap="xs">
                                        <Text
                                          size="xs"
                                          fw={600}
                                          c="dimmed"
                                          tt="uppercase"
                                          style={{
                                            letterSpacing: "0.05em",
                                            flexShrink: 0,
                                          }}
                                        >
                                          Email
                                        </Text>
                                        <Text
                                          size="sm"
                                          style={{ whiteSpace: "nowrap" }}
                                        >
                                          {practice.email}
                                        </Text>
                                      </Group>
                                    )}
                                    {/* Phone, Fax, Address — compact grid */}
                                    <SimpleGrid
                                      cols={{ base: 2, sm: 4 }}
                                      spacing="xs"
                                    >
                                      <Box>
                                        <Text
                                          size="xs"
                                          fw={600}
                                          c="dimmed"
                                          tt="uppercase"
                                          style={{ letterSpacing: "0.05em" }}
                                        >
                                          Phone
                                        </Text>
                                        <Text size="sm">
                                          {practice.primaryPhone || "—"}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text
                                          size="xs"
                                          fw={600}
                                          c="dimmed"
                                          tt="uppercase"
                                          style={{ letterSpacing: "0.05em" }}
                                        >
                                          Secondary Phone
                                        </Text>
                                        <Text size="sm">
                                          {practice.secondaryPhone || "—"}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text
                                          size="xs"
                                          fw={600}
                                          c="dimmed"
                                          tt="uppercase"
                                          style={{ letterSpacing: "0.05em" }}
                                        >
                                          Fax
                                        </Text>
                                        <Text size="sm">
                                          {practice.fax || "—"}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text
                                          size="xs"
                                          fw={600}
                                          c="dimmed"
                                          tt="uppercase"
                                          style={{ letterSpacing: "0.05em" }}
                                        >
                                          Address
                                        </Text>
                                        <Text size="sm">
                                          {practice.address || "—"}
                                        </Text>
                                      </Box>
                                    </SimpleGrid>
                                  </Stack>
                                </Group>

                                <Tabs
                                  value={activeTab}
                                  onChange={(tab) =>
                                    updateParams({
                                      tab:
                                        tab && tab !== "portal-access"
                                          ? tab
                                          : null,
                                    })
                                  }
                                >
                                  <Tabs.List>
                                    <Tabs.Tab value="portal-access">
                                      Portal Access
                                    </Tabs.Tab>
                                    <Tabs.Tab value="physicians">
                                      Physicians ({practicePhysicians.length})
                                    </Tabs.Tab>
                                    <Tabs.Tab value="report-delivery">
                                      Report Delivery (
                                      {practiceDeliveryMethods.length})
                                    </Tabs.Tab>
                                  </Tabs.List>

                                  {/* Portal Access Tab */}
                                  <Tabs.Panel value="portal-access" pt="md">
                                    <PortalAccessSection
                                      subjectType="practice"
                                      subjectId={practice.id}
                                      subjectName={practice.name}
                                      subjectEmail={practice.email ?? null}
                                    />
                                  </Tabs.Panel>

                                  {/* Physicians Tab */}
                                  <Tabs.Panel value="physicians" pt="md">
                                    {duplicatePhysicianNames.size > 0 && (
                                      <Alert
                                        icon={<IconAlertCircle size={16} />}
                                        color="orange"
                                        variant="light"
                                        mb="md"
                                        title="Potential duplicate physicians detected"
                                      >
                                        <Text size="sm">
                                          Multiple physician records with the
                                          same name are linked to this practice.
                                          Please review these records and ensure
                                          they refer to distinct individuals;
                                          identical duplicates can cause issues
                                          with reporting and billing.
                                        </Text>
                                      </Alert>
                                    )}
                                    <Group justify="flex-end" mb="sm">
                                      <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={handleOpenPhysicianModal}
                                        disabled={
                                          availablePhysicians.length === 0
                                        }
                                      >
                                        Link Physician
                                      </Button>
                                    </Group>
                                    {isLoadingPhysicians ? (
                                      <Center py="md">
                                        <Loader size="sm" />
                                      </Center>
                                    ) : (
                                      <Stack gap="sm">
                                        {practicePhysicians.length === 0 ? (
                                          <Text size="sm" c="dimmed">
                                            No physicians linked to this
                                            practice.
                                          </Text>
                                        ) : (
                                          <Table>
                                            <Table.Thead>
                                              <Table.Tr>
                                                <Table.Th>Physician</Table.Th>
                                                <Table.Th>NPI</Table.Th>
                                                <Table.Th>Email</Table.Th>
                                                <Table.Th
                                                  style={{ width: 80 }}
                                                />
                                              </Table.Tr>
                                            </Table.Thead>
                                            <Table.Tbody>
                                              {practicePhysicians.map((pp) => (
                                                <Table.Tr key={pp.id}>
                                                  <Table.Td>
                                                    <Group gap="xs">
                                                      <Text size="sm" fw={500}>
                                                        {pp.physician.firstName}{" "}
                                                        {pp.physician.lastName}
                                                      </Text>
                                                      {duplicatePhysicianNames.has(
                                                        `${pp.physician.firstName} ${pp.physician.lastName}`.toLowerCase()
                                                      ) && (
                                                        <Tooltip label="Multiple records with this name are linked to this practice">
                                                          <IconAlertCircle
                                                            size={14}
                                                            color="var(--mantine-color-orange-6)"
                                                          />
                                                        </Tooltip>
                                                      )}
                                                      {pp.isPrimary && (
                                                        <Badge
                                                          size="xs"
                                                          variant="light"
                                                          color="blue"
                                                        >
                                                          Primary
                                                        </Badge>
                                                      )}
                                                    </Group>
                                                  </Table.Td>
                                                  <Table.Td>
                                                    <Text size="sm" c="dimmed">
                                                      {pp.physician.npi || "—"}
                                                    </Text>
                                                  </Table.Td>
                                                  <Table.Td>
                                                    <Text size="sm" c="dimmed">
                                                      {pp.physician.email ||
                                                        "—"}
                                                    </Text>
                                                  </Table.Td>
                                                  <Table.Td>
                                                    <Group gap="xs">
                                                      {!pp.isPrimary && (
                                                        <Tooltip label="Set as Primary">
                                                          <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            onClick={() =>
                                                              handleSetPrimaryPhysician(
                                                                pp.physicianId
                                                              )
                                                            }
                                                          >
                                                            <IconStar
                                                              size={14}
                                                            />
                                                          </ActionIcon>
                                                        </Tooltip>
                                                      )}
                                                      {pp.isPrimary && (
                                                        <ActionIcon
                                                          size="sm"
                                                          variant="subtle"
                                                          color="yellow"
                                                          disabled
                                                        >
                                                          <IconStarFilled
                                                            size={14}
                                                          />
                                                        </ActionIcon>
                                                      )}
                                                      <Tooltip label="Unlink">
                                                        <ActionIcon
                                                          size="sm"
                                                          variant="subtle"
                                                          color="red"
                                                          onClick={() =>
                                                            handleConfirmDelete(
                                                              "physician",
                                                              pp.physicianId,
                                                              `${pp.physician.firstName} ${pp.physician.lastName}`
                                                            )
                                                          }
                                                        >
                                                          <IconTrash
                                                            size={14}
                                                          />
                                                        </ActionIcon>
                                                      </Tooltip>
                                                    </Group>
                                                  </Table.Td>
                                                </Table.Tr>
                                              ))}
                                            </Table.Tbody>
                                          </Table>
                                        )}
                                      </Stack>
                                    )}
                                  </Tabs.Panel>

                                  {/* Report Delivery Tab */}
                                  <Tabs.Panel value="report-delivery" pt="md">
                                    <Group justify="flex-end" mb="sm">
                                      <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={() =>
                                          handleOpenDeliveryModal()
                                        }
                                      >
                                        Add Delivery Method
                                      </Button>
                                    </Group>
                                    {isLoadingDelivery ? (
                                      <Center py="md">
                                        <Loader size="sm" />
                                      </Center>
                                    ) : (
                                      <Stack gap="sm">
                                        {practiceDeliveryMethods.length ===
                                        0 ? (
                                          <Text size="sm" c="dimmed">
                                            No delivery methods configured.
                                          </Text>
                                        ) : (
                                          practiceDeliveryMethods.map((pdm) => {
                                            const pdmMethodName =
                                              pdm.reportDeliveryMethod?.name?.toLowerCase() ||
                                              "";
                                            const isHl7Pdm =
                                              pdmMethodName.startsWith("hl7");
                                            const isPortalPdm =
                                              pdmMethodName.startsWith(
                                                "portal"
                                              );
                                            const isEmailPdm =
                                              pdmMethodName.startsWith("email");
                                            const isFaxPdm =
                                              pdmMethodName.startsWith("fax");
                                            const isShipPdm =
                                              pdmMethodName.startsWith(
                                                "ship"
                                              ) || pdmMethodName === "mail";
                                            const isRemotePrintPdm =
                                              pdmMethodName.includes(
                                                "remote print"
                                              );

                                            const destination = isHl7Pdm
                                              ? [
                                                  pdm.hl7EndpointHost &&
                                                  pdm.hl7EndpointPort
                                                    ? `${pdm.hl7EndpointHost}:${pdm.hl7EndpointPort}`
                                                    : pdm.hl7EndpointHost || "",
                                                  pdm.hl7FacilityId
                                                    ? `facility: ${pdm.hl7FacilityId}`
                                                    : "",
                                                  pdm.hl7ApplicationId
                                                    ? `app: ${pdm.hl7ApplicationId}`
                                                    : "",
                                                ]
                                                  .filter(Boolean)
                                                  .join(" · ")
                                              : isPortalPdm || isEmailPdm
                                              ? pdm.email || ""
                                              : isFaxPdm
                                              ? pdm.fax || ""
                                              : isRemotePrintPdm
                                              ? pdm.remoteConnectionIdentifier ||
                                                ""
                                              : isShipPdm
                                              ? [
                                                  pdm.address || "",
                                                  pdm.phone || "",
                                                ]
                                                  .filter(Boolean)
                                                  .join(" · ")
                                              : pdm.fax ||
                                                pdm.email ||
                                                pdm.address ||
                                                "";
                                            return (
                                              <Group
                                                key={pdm.id}
                                                justify="space-between"
                                              >
                                                <Group
                                                  gap="xs"
                                                  style={{
                                                    minWidth: 0,
                                                    flex: 1,
                                                  }}
                                                >
                                                  <Text
                                                    size="sm"
                                                    fw={500}
                                                    style={{ flexShrink: 0 }}
                                                  >
                                                    {
                                                      pdm.reportDeliveryMethod
                                                        ?.name
                                                    }
                                                  </Text>
                                                  {destination && (
                                                    <Text
                                                      size="xs"
                                                      c="dimmed"
                                                      truncate="end"
                                                      style={{
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                      }}
                                                    >
                                                      {destination}
                                                    </Text>
                                                  )}
                                                </Group>
                                                <Group gap="xs">
                                                  <Badge
                                                    size="xs"
                                                    color={
                                                      pdm.isActive
                                                        ? "green"
                                                        : "red"
                                                    }
                                                    variant="light"
                                                  >
                                                    {pdm.isActive
                                                      ? "Active"
                                                      : "Inactive"}
                                                  </Badge>
                                                  <Tooltip label="Edit">
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      onClick={() =>
                                                        handleOpenDeliveryModal(
                                                          pdm
                                                        )
                                                      }
                                                    >
                                                      <IconEdit size={14} />
                                                    </ActionIcon>
                                                  </Tooltip>
                                                  <Tooltip
                                                    label={
                                                      pdm.isActive
                                                        ? "Deactivate"
                                                        : "Activate"
                                                    }
                                                  >
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      color={
                                                        pdm.isActive
                                                          ? "red"
                                                          : "green"
                                                      }
                                                      onClick={() =>
                                                        handleToggleDeliveryActive(
                                                          pdm.id
                                                        )
                                                      }
                                                    >
                                                      {pdm.isActive ? (
                                                        <IconX size={14} />
                                                      ) : (
                                                        <IconCheck size={14} />
                                                      )}
                                                    </ActionIcon>
                                                  </Tooltip>
                                                  <Tooltip label="Remove">
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      color="red"
                                                      onClick={() =>
                                                        handleConfirmDelete(
                                                          "delivery",
                                                          pdm.id,
                                                          pdm
                                                            .reportDeliveryMethod
                                                            ?.name || ""
                                                        )
                                                      }
                                                    >
                                                      <IconTrash size={14} />
                                                    </ActionIcon>
                                                  </Tooltip>
                                                </Group>
                                              </Group>
                                            );
                                          })
                                        )}
                                      </Stack>
                                    )}
                                  </Tabs.Panel>
                                </Tabs>
                              </Box>
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Fragment>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Practice Modal */}
        <Modal.Root
          opened={practiceModalOpened}
          onClose={closePracticeModal}
          size="lg"
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editingPractice ? "Edit Practice" : "Add New Practice"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              {editingPractice && isLoadingEditData ? (
                <Center py="xl">
                  <Loader size="sm" />
                </Center>
              ) : (
                <PracticeForm
                  key={
                    editingPractice
                      ? `${editingPractice.id}-${
                          isLoadingEditData ? "loading" : "ready"
                        }`
                      : "new"
                  }
                  formId="practice-form-modal"
                  hideFooter
                  initialValues={editingPractice || undefined}
                  onSubmit={handlePracticeSubmit}
                />
              )}
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button
                variant="default"
                onClick={closePracticeModal}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="practice-form-modal"
                loading={createPractice.isPending || updatePractice.isPending}
              >
                {editingPractice ? "Update" : "Create"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Link Physician Modal */}
        <Modal.Root
          opened={physicianModalOpened}
          onClose={closePhysicianModal}
          size="md"
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Link Physician</Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <Stack gap="md">
                <Select
                  label="Physician"
                  placeholder="Select a physician..."
                  data={availablePhysicians.map((p) => ({
                    value: p.id,
                    label: `${p.firstName} ${p.lastName}${
                      p.npi ? ` (NPI: ${p.npi})` : ""
                    }`,
                  }))}
                  value={selectedPhysicianId}
                  onChange={(value) => setSelectedPhysicianId(value || "")}
                  searchable
                  withAsterisk
                />
                <Checkbox
                  label="Set as primary physician for this practice"
                  checked={physicianIsPrimary}
                  onChange={(e) =>
                    setPhysicianIsPrimary(e.currentTarget.checked)
                  }
                />
              </Stack>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closePhysicianModal}>
                Cancel
              </Button>
              <Button
                loading={addPhysician.isPending}
                onClick={() => {
                  if (!selectedPhysicianId) {
                    notifications.show({
                      color: "orange",
                      message: "Please select a physician",
                    });
                    return;
                  }
                  void handleAddPhysician();
                }}
              >
                Link Physician
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Add/Edit Delivery Method Modal */}
        <Modal.Root
          opened={deliveryModalOpened}
          onClose={handleCloseDeliveryModal}
          size={methodName.startsWith("hl7") ? "lg" : "md"}
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editingDelivery
                  ? "Edit Delivery Method"
                  : "Add Delivery Method"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <form id="delivery-form" onSubmit={handleDeliverySubmit}>
                <Stack gap="md">
                  <Select
                    label="Delivery Method"
                    placeholder="Select delivery method..."
                    data={deliveryMethods
                      .filter((m) => m.isActive)
                      .map((m) => ({
                        value: m.id,
                        label: m.name,
                      }))}
                    withAsterisk
                    {...deliveryForm.getInputProps("reportDeliveryMethodId")}
                  />

                  {portalIsPending && (
                    <Alert
                      icon={<IconInfoCircle size={16} />}
                      color="blue"
                      variant="light"
                      title="Portal invitation pending"
                      role="status"
                    >
                      <Text size="sm">
                        This practice has portal contacts, but none have
                        activated their accounts yet. Reports will be available
                        in the portal once a contact completes registration.
                      </Text>
                    </Alert>
                  )}

                  {portalNeedsSetup && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="yellow"
                      variant="light"
                      title="Set up portal access first"
                      role="alert"
                    >
                      <Stack gap="sm">
                        <Text size="sm">
                          This practice has no active or invited portal access
                          grants yet. Add at least one portal contact so the
                          Portal delivery method has a recipient to route to.
                        </Text>
                        <Group>
                          <Button
                            size="xs"
                            variant="filled"
                            color="yellow"
                            onClick={handleOpenPortalGrantModal}
                          >
                            Set up portal access
                          </Button>
                        </Group>
                      </Stack>
                    </Alert>
                  )}

                  {/* Conditional fields based on method type */}
                  {methodName === "email" && (
                    <TextInput
                      label="Email Address"
                      placeholder="Enter email address"
                      {...deliveryForm.getInputProps("email")}
                    />
                  )}

                  {methodName === "fax" && (
                    <TextInput
                      label="Fax Number"
                      placeholder="Enter fax number"
                      {...deliveryForm.getInputProps("fax")}
                    />
                  )}

                  {(methodName.startsWith("ship") || methodName === "mail") && (
                    <Textarea
                      label="Mailing Address / Shipping Address"
                      placeholder="Enter full address"
                      autosize
                      minRows={2}
                      maxRows={4}
                      {...deliveryForm.getInputProps("address")}
                    />
                  )}

                  {methodName.includes("remote print") && (
                    <TextInput
                      label="Connection Identifier"
                      placeholder="e.g. clinic-printer-001"
                      description="Must match the identifier in the Remote Print client app at the clinic"
                      withAsterisk
                      {...deliveryForm.getInputProps(
                        "remoteConnectionIdentifier"
                      )}
                    />
                  )}

                  {methodName.startsWith("portal") && (
                    <Text size="sm" c="dimmed">
                      Reports will be routed to the practice's portal. Manage
                      portal contacts in the Portal Access section below.
                    </Text>
                  )}

                  {methodName.startsWith("hl7") && (
                    <>
                      <Group grow>
                        <TextInput
                          label="HL7 Endpoint Host"
                          placeholder="e.g. 192.168.1.100 or ehr.hospital.com"
                          withAsterisk
                          {...deliveryForm.getInputProps("hl7EndpointHost")}
                        />
                        <NumberInput
                          label="HL7 Endpoint Port"
                          placeholder="e.g. 2575"
                          min={1}
                          max={65535}
                          withAsterisk
                          {...deliveryForm.getInputProps("hl7EndpointPort")}
                        />
                      </Group>
                      <Group grow>
                        <TextInput
                          label="HL7 Facility ID"
                          placeholder="Receiving facility identifier"
                          {...deliveryForm.getInputProps("hl7FacilityId")}
                        />
                        <TextInput
                          label="HL7 Application ID"
                          placeholder="Receiving application identifier"
                          {...deliveryForm.getInputProps("hl7ApplicationId")}
                        />
                      </Group>
                    </>
                  )}

                  {(methodName.startsWith("ship") || methodName === "mail") && (
                    <TextInput
                      label="Phone (optional)"
                      placeholder="Enter phone number"
                      {...deliveryForm.getInputProps("phone")}
                    />
                  )}
                </Stack>
              </form>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={handleCloseDeliveryModal}>
                Cancel
              </Button>
              {!portalNeedsSetup && (
                <Button
                  type="submit"
                  form="delivery-form"
                  loading={
                    addDeliveryMethod.isPending ||
                    updateDeliveryMethod.isPending
                  }
                >
                  {editingDelivery ? "Update" : "Add"}
                </Button>
              )}
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Portal Access setup — opened from the delivery-method modal when the
            admin tries to use Portal but the practice has no active grants */}
        {expandedRow && (
          <AddPortalGrantModal
            opened={portalGrantModalOpened}
            onClose={handleClosePortalGrantModal}
            subjectType="practice"
            subjectId={expandedRow}
            subjectName={
              practices.find((p) => p.id === expandedRow)?.name || undefined
            }
            mode="primary"
            primaryDefaults={{
              email: practices.find((p) => p.id === expandedRow)?.email ?? "",
              firstName: "",
              lastName: "",
              role: "Contact Person",
            }}
          />
        )}

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Practices"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="Select status..."
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: (value as "all" | "active" | "inactive") || "all",
                })
              }
            />

            <Select
              label="Preferred Pathologist"
              placeholder="Any pathologist..."
              data={[
                { value: "", label: "Any" },
                ...pathologists
                  .filter((p) => p.fullName)
                  .map((p) => ({
                    value: p.id,
                    label: p.fullName as string,
                  })),
              ]}
              value={pendingFilters.preferredPathologistId || ""}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  preferredPathologistId: value || "",
                })
              }
              searchable
              clearable
            />

            <Group justify="space-between" mt="md">
              <Button
                variant="subtle"
                onClick={() =>
                  setPendingFilters({
                    status: "all",
                    preferredPathologistId: "",
                  })
                }
              >
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal.Root
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          size="sm"
          centered
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Confirm Removal</Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <Stack gap="md">
                <Text>
                  Are you sure you want to remove{" "}
                  <strong>{itemToDelete?.name}</strong> from this practice?
                </Text>
                <Text size="sm" c="dimmed">
                  This action cannot be undone.
                </Text>
              </Stack>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={() => void handleDelete()}
                loading={
                  removePhysician.isPending || removeDeliveryMethod.isPending
                }
              >
                Remove
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>
      </Box>
    </Container>
  );
}
